/** XeAgents.ai**/
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, Redirect } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";
import { formatRelativeTime } from "@/lib/date-format";
import {
  Plus,
  Ticket,
  Clock,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Search,
  Filter,
  RefreshCw,
  ChevronRight,
  User,
  Calendar,
  Tag,
  AlertTriangle,
  XCircle,
  Loader2,
} from "lucide-react";

interface SupportTicket {
  id: string;
  ticketNumber: number;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  resolution: string | null;
  attachments: string[];
  resolvedAt: string | null;
  closedAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  organizationId?: string;
  userName?: string;
  userEmail?: string;
  organizationName?: string;
  assignedTo?: string | null;
}

interface TicketsResponse {
  tickets: SupportTicket[];
  total: number;
  limit: number;
  offset: number;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  open: { label: "Open", color: "bg-blue-500", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-yellow-500", icon: Clock },
  waiting_on_customer: { label: "Waiting on Customer", color: "bg-purple-500", icon: MessageSquare },
  resolved: { label: "Resolved", color: "bg-green-500", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-gray-500", icon: XCircle },
};

const priorityConfig: Record<string, { label: string; color: string }> = {
  low: { label: "Low", color: "bg-gray-400" },
  medium: { label: "Medium", color: "bg-blue-400" },
  high: { label: "High", color: "bg-orange-500" },
  urgent: { label: "Urgent", color: "bg-red-500" },
};

const categoryConfig: Record<string, { label: string }> = {
  general: { label: "General" },
  billing: { label: "Billing" },
  technical: { label: "Technical" },
  feature_request: { label: "Feature Request" },
  bug_report: { label: "Bug Report" },
  account: { label: "Account" },
};

export default function HelpDeskPage() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const currentUser = AuthStorage.getUser();
  const isSuperAdmin = AuthStorage.isSuperAdmin();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    category: "general",
    priority: "medium",
  });

  // Fetch user's tickets
  const { data: ticketsData, isLoading, isError, error, refetch } = useQuery<TicketsResponse>({
    queryKey: ["/api/helpdesk/tickets", statusFilter],
    queryFn: async () => {
      const url = statusFilter !== "all"
        ? `/api/helpdesk/tickets?status=${statusFilter}`
        : "/api/helpdesk/tickets";
      console.log("[HelpDesk] Fetching tickets from:", url);
      const authHeader = AuthStorage.getAuthHeader();
      const res = await fetch(url, {
        headers: authHeader ? { Authorization: authHeader } : {},
        credentials: "include",
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch tickets: ${res.status}`);
      }
      return res.json();
    },
  });

  // Create ticket mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/helpdesk/tickets", formData);
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: t("helpdesk.ticketCreated"),
        description: t("helpdesk.ticketCreatedDesc", { number: data.ticketNumber }),
      });
      setCreateDialogOpen(false);
      setFormData({ subject: "", description: "", category: "general", priority: "medium" });
      queryClient.invalidateQueries({ predicate: (query) =>
        query.queryKey[0]?.toString().startsWith("/api/helpdesk/tickets")
      });
    },
    onError: (error: any) => {
      toast({
        title: t("helpdesk.error"),
        description: error.message || t("helpdesk.createFailed"),
        variant: "destructive",
      });
    },
  });

  const tickets = ticketsData?.tickets || [];
  const filteredTickets = searchQuery
    ? tickets.filter(
        (t) =>
          t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.ticketNumber.toString().includes(searchQuery)
      )
    : tickets;

  // Redirect super admins to the admin helpdesk view
  if (isSuperAdmin) {
    return <Redirect to="/admin/helpdesk" />;
  }

  const handleCreateTicket = () => {
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast({
        title: t("helpdesk.error"),
        description: t("helpdesk.subjectDescriptionRequired"),
        variant: "destructive",
      });
      return;
    }
    createMutation.mutate();
  };

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const config = priorityConfig[priority] || priorityConfig.medium;
    return (
      <Badge variant="outline" className={`border-0 ${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="h-6 w-6" />
            {t("helpdesk.title")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("helpdesk.description")}
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("helpdesk.createTicket")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.totalTickets")}</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ticketsData?.total || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.openTickets")}</CardTitle>
            <AlertCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tickets.filter((t) => t.status === "open" || t.status === "in_progress").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.resolvedTickets")}</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tickets.filter((t) => t.status === "resolved").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("helpdesk.waitingResponse")}</CardTitle>
            <MessageSquare className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tickets.filter((t) => t.status === "waiting_on_customer").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-lg">{t("helpdesk.yourTickets")}</CardTitle>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t("helpdesk.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t("helpdesk.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("helpdesk.allStatuses")}</SelectItem>
                  <SelectItem value="open">{t("helpdesk.statusOpen")}</SelectItem>
                  <SelectItem value="in_progress">{t("helpdesk.statusInProgress")}</SelectItem>
                  <SelectItem value="waiting_on_customer">{t("helpdesk.statusWaiting")}</SelectItem>
                  <SelectItem value="resolved">{t("helpdesk.statusResolved")}</SelectItem>
                  <SelectItem value="closed">{t("helpdesk.statusClosed")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-red-600">{t("helpdesk.errorLoading")}</h3>
              <p className="text-muted-foreground mt-1">
                {error?.message || t("helpdesk.errorLoadingDesc")}
              </p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("helpdesk.tryAgain")}
              </Button>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-8">
              <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">{t("helpdesk.noTickets")}</h3>
              <p className="text-muted-foreground mt-1">{t("helpdesk.noTicketsDesc")}</p>
              <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t("helpdesk.createFirstTicket")}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">#</TableHead>
                  <TableHead>{t("helpdesk.subject")}</TableHead>
                  <TableHead>{t("helpdesk.category")}</TableHead>
                  <TableHead>{t("helpdesk.priority")}</TableHead>
                  <TableHead>{t("helpdesk.status")}</TableHead>
                  <TableHead>{t("helpdesk.lastActivity")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setLocation(`/app/helpdesk/${ticket.id}`)}
                  >
                    <TableCell className="font-mono text-sm">
                      #{ticket.ticketNumber}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{ticket.subject}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-xs">
                        {ticket.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {categoryConfig[ticket.category]?.label || ticket.category}
                      </Badge>
                    </TableCell>
                    <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                    <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(ticket.lastActivityAt)}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("helpdesk.createTicket")}</DialogTitle>
            <DialogDescription>
              {t("helpdesk.createTicketDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">{t("helpdesk.subject")} *</Label>
              <Input
                id="subject"
                placeholder={t("helpdesk.subjectPlaceholder")}
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">{t("helpdesk.category")}</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{t("helpdesk.categoryGeneral")}</SelectItem>
                    <SelectItem value="billing">{t("helpdesk.categoryBilling")}</SelectItem>
                    <SelectItem value="technical">{t("helpdesk.categoryTechnical")}</SelectItem>
                    <SelectItem value="feature_request">{t("helpdesk.categoryFeature")}</SelectItem>
                    <SelectItem value="bug_report">{t("helpdesk.categoryBug")}</SelectItem>
                    <SelectItem value="account">{t("helpdesk.categoryAccount")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">{t("helpdesk.priority")}</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t("helpdesk.priorityLow")}</SelectItem>
                    <SelectItem value="medium">{t("helpdesk.priorityMedium")}</SelectItem>
                    <SelectItem value="high">{t("helpdesk.priorityHigh")}</SelectItem>
                    <SelectItem value="urgent">{t("helpdesk.priorityUrgent")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t("common.description")} *</Label>
              <Textarea
                id="description"
                placeholder={t("helpdesk.descriptionPlaceholder")}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleCreateTicket} disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("helpdesk.submitTicket")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
